/* ============================================================
   D.A.D Homepage JS
   Loaded only on index template.
   - Signature parallax brand sections (sticky image + glide text)
   - Sticky CTA (show after X pixels, close persistence)
   - Header: transparent over hero, opaque after scroll
   - Mobile menu toggle
   ============================================================ */
window.DAD = window.DAD || {};
window.DAD.homepage = window.DAD.homepage || {};

(function () {
  "use strict";

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Parallax brand (signature) ---------- */
  function initParallax() {
    if (prefersReduced) return;
    const sections = Array.from(document.querySelectorAll("[data-parallax-brand]"));
    if (sections.length === 0) return;

    const items = sections.map((section) => ({
      section,
      media: section.querySelector("[data-parallax-media]"),
      overlay: section.querySelector("[data-parallax-overlay]"),
      text: section.querySelector("[data-parallax-text]"),
    }));

    let ticking = false;

    function update() {
      const winH = window.innerHeight;
      for (const it of items) {
        const rect = it.section.getBoundingClientRect();
        const sectionH = it.section.offsetHeight;
        // Image scale + overlay opacity tied to (winH - rect.bottom) / winH
        const phase1 = clamp((winH - rect.bottom) / winH, 0, 1);
        const scale = 1 - phase1 * 0.15;
        const overlayOpacity = 0.45 + phase1 * 0.4;
        if (it.media) it.media.style.transform = `scale(${scale.toFixed(4)})`;
        if (it.overlay) it.overlay.style.opacity = overlayOpacity.toFixed(3);

        // Text glide tied to (winH - rect.top) / (winH + sectionH)
        const progress = clamp((winH - rect.top) / (winH + sectionH), 0, 1);
        const translateY = 250 - progress * 500;
        const opacity = 1 - Math.abs(progress - 0.5) * 2;
        if (it.text) {
          it.text.style.transform = `translateY(${translateY.toFixed(1)}px)`;
          it.text.style.opacity = Math.max(0, opacity).toFixed(3);
        }
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
  }

  /* ---------- Header: subtle scroll effect (always opaque) ---------- */
  function initHeader() {
    const header = document.querySelector("[data-site-header]");
    if (!header) return;
    function check() {
      header.classList.toggle("is-scrolled", window.scrollY > 16);
    }
    let scrollTicking = false;
    function onScroll() {
      if (!scrollTicking) {
        window.requestAnimationFrame(() => { check(); scrollTicking = false; });
        scrollTicking = true;
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    check();
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    const toggle = document.querySelector("[data-mobile-toggle]");
    const menu = document.querySelector("[data-mobile-menu]");
    if (!toggle || !menu) return;

    function openMenu() {
      const header = document.querySelector("[data-site-header]");
      if (header) menu.style.top = header.getBoundingClientRect().bottom + "px";
      menu.removeAttribute("hidden");
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.add("is-open");
      document.body.classList.add("mobile-menu-open");
    }
    function closeMenu() {
      menu.setAttribute("hidden", "");
      toggle.setAttribute("aria-expanded", "false");
      toggle.classList.remove("is-open");
      document.body.classList.remove("mobile-menu-open");
    }

    toggle.addEventListener("click", () => {
      if (menu.hasAttribute("hidden")) openMenu(); else closeMenu();
    });
    // Fermer quand on tape un lien (navigation propre, pas de menu reste ouvert)
    menu.querySelectorAll("a").forEach((a) => a.addEventListener("click", closeMenu));
    // Fermer avec Echap
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !menu.hasAttribute("hidden")) closeMenu();
    });
  }

  /* ---------- Sticky CTA ---------- */
  function initStickyCta() {
    const bar = document.querySelector("[data-sticky-cta]");
    if (!bar) return;
    if (sessionStorage.getItem("dadStickyCtaClosed") === "1") {
      bar.remove();
      return;
    }
    const threshold = parseInt(bar.dataset.showAfter || "600", 10);
    const closeBtn = bar.querySelector("[data-sticky-cta-close]");

    function check() {
      const visible = window.scrollY > threshold;
      bar.classList.toggle("is-visible", visible);
      bar.setAttribute("aria-hidden", visible ? "false" : "true");
    }

    let ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => { check(); ticking = false; });
        ticking = true;
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    check();

    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        sessionStorage.setItem("dadStickyCtaClosed", "1");
        bar.classList.remove("is-visible");
        setTimeout(() => bar.remove(), 300);
      });
    }
  }

  /* ---------- Quick add (homepage product cards) ---------- */
  function initQuickAdd() {
    document.querySelectorAll("[data-quick-add-form]").forEach((form) => {
      form.addEventListener("submit", async (e) => {
        // Default: fall through to /cart/add page. Try AJAX with graceful fallback.
        if (!window.fetch) return;
        e.preventDefault();
        const btn = form.querySelector("button");
        const original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = "Ajout…";
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
            body: new URLSearchParams(new FormData(form)).toString(),
          });
          if (!res.ok) throw new Error("add failed");
          btn.innerHTML = "Ajouté ✓";
          updateCartCount();
          setTimeout(() => { btn.innerHTML = original; btn.disabled = false; }, 1600);
        } catch (err) {
          window.location.href = "/cart";
        }
      });
    });
  }

  function updateCartCount() {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((cart) => {
        const el = document.querySelector("[data-cart-count]");
        if (el) {
          el.textContent = cart.item_count;
          el.classList.toggle("is-empty", cart.item_count === 0);
        }
      })
      .catch(() => {});
  }

  /* ---------- Utils ---------- */
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  /* ---------- Popup welcome ---------- */
  function initPopupWelcome() {
    const root = document.querySelector("[data-dad-popup-welcome-root]");
    const popup = document.querySelector("[data-dad-popup-welcome]");
    if (!root || !popup) return;
    if (sessionStorage.getItem("dadPopupSeen") === "1") return;

    const delayAttr = parseInt(root.dataset.delay || "5", 10);
    const delayMs = Math.max(0, delayAttr) * 1000;
    const exitIntent = root.dataset.exitIntent === "1";
    const closers = popup.querySelectorAll("[data-dad-popup-close]");
    let isOpen = false;
    let openedOnce = false;
    let timerId = null;

    function open() {
      if (openedOnce || isOpen) return;
      isOpen = true;
      openedOnce = true;
      sessionStorage.setItem("dadPopupSeen", "1");
      popup.hidden = false;
      popup.setAttribute("aria-hidden", "false");
      document.body.classList.add("dad-popup-open");
      // next frame to trigger transition
      requestAnimationFrame(() => popup.classList.add("is-open"));
      cleanupTriggers();
      const firstInput = popup.querySelector("input[type=email]");
      if (firstInput) setTimeout(() => firstInput.focus(), 280);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      popup.classList.remove("is-open");
      popup.setAttribute("aria-hidden", "true");
      document.body.classList.remove("dad-popup-open");
      setTimeout(() => { popup.hidden = true; }, 280);
    }

    closers.forEach((el) => el.addEventListener("click", close));
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isOpen) close();
    });

    function onExitIntent(e) {
      if (e.clientY <= 0 && !openedOnce) open();
    }

    function cleanupTriggers() {
      if (timerId) { clearTimeout(timerId); timerId = null; }
      document.removeEventListener("mouseleave", onExitIntent);
    }

    // Delay trigger
    timerId = setTimeout(() => { if (!openedOnce) open(); }, delayMs);

    // Exit intent (desktop only)
    if (exitIntent && window.matchMedia("(hover: hover)").matches) {
      document.addEventListener("mouseleave", onExitIntent);
    }

    // If the form was just submitted successfully, surface it immediately
    if (popup.querySelector(".form-message--success")) {
      open();
    }
  }

  /* ---------- Recently viewed ---------- */
  const RV_KEY = "dadRecentlyViewed";

  function readRecentlyViewed() {
    try {
      const raw = localStorage.getItem(RV_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecentlyViewed(handles) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(handles)); }
    catch (e) { /* quota or disabled */ }
  }

  function trackRecentlyViewed() {
    // 1) If we're on a product page (body has product handle attribute)
    const productHandle = document.body && document.body.dataset && document.body.dataset.productHandle;
    if (productHandle) addToRecentlyViewed(productHandle);

    // 2) Also track clicks from product cards (homepage / collection)
    document.querySelectorAll("[data-product-card]").forEach((card) => {
      const handle = card.getAttribute("data-product-handle");
      const link = card.querySelector("a[href*='/products/']");
      if (!handle && !link) return;
      card.addEventListener("click", () => {
        let h = handle;
        if (!h && link) {
          const match = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
          if (match) h = match[1];
        }
        if (h) addToRecentlyViewed(h);
      }, { capture: true });
    });
  }

  function addToRecentlyViewed(handle) {
    if (!handle) return;
    let list = readRecentlyViewed().filter((h) => h !== handle);
    list.unshift(handle);
    if (list.length > 12) list = list.slice(0, 12);
    writeRecentlyViewed(list);
  }

  function renderRecentlyViewed() {
    const section = document.querySelector("[data-recently-viewed]");
    if (!section) return;
    const grid = section.querySelector("[data-recently-viewed-grid]");
    const tpl = section.querySelector("[data-recently-viewed-template]");
    if (!grid || !tpl) return;

    const limit = parseInt(section.dataset.limit || "4", 10);
    const handles = readRecentlyViewed();

    // Exclude current product if on PDP
    const currentHandle = document.body && document.body.dataset && document.body.dataset.productHandle;
    const filtered = handles.filter((h) => h !== currentHandle).slice(0, limit);
    if (filtered.length === 0) return;

    let rendered = 0;
    const promises = filtered.map((handle) =>
      fetch("/products/" + handle + ".js", { headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    );

    Promise.all(promises).then((products) => {
      products.forEach((p) => {
        if (!p) return;
        const node = tpl.content.firstElementChild.cloneNode(true);
        node.setAttribute("data-product-handle", p.handle);
        const link = node.querySelector("[data-rv-link]");
        const img = node.querySelector("[data-rv-image]");
        const brand = node.querySelector("[data-rv-brand]");
        const title = node.querySelector("[data-rv-title]");
        const price = node.querySelector("[data-rv-price]");
        if (link) link.setAttribute("href", p.url);
        if (img) {
          const imageSrc = p.featured_image || (p.images && p.images[0]) || "";
          if (imageSrc) { img.src = imageSrc; img.alt = p.title; }
          else { img.remove(); }
        }
        if (brand) {
          if (p.vendor) brand.textContent = p.vendor;
          else brand.remove();
        }
        if (title) title.textContent = p.title;
        if (price) price.textContent = formatMoney(p.price);
        grid.appendChild(node);
        rendered += 1;
      });
      if (rendered > 0) section.hidden = false;
    });
  }

  function formatMoney(cents) {
    if (typeof cents !== "number") return "";
    const amount = (cents / 100).toFixed(2);
    return amount.replace(".", ",") + " $";
  }

  function init() {
    initHeader();
    // initMobileMenu() est maintenant géré globalement par dad-product.js
    // (chargé sur toutes les pages) — sinon le burger ne marche pas hors accueil.
    initParallax();
    initStickyCta();
    // initQuickAdd() retiré : l'ajout au panier est géré globalement par dad-product.js
    // (AJAX + ouverture du tiroir). Évite le double-ajout sur la page d'accueil.
    initPopupWelcome();
    trackRecentlyViewed();
    renderRecentlyViewed();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.DAD.homepage.init = init;
})();
